import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Check } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import type { Estado } from '@/lib/types';
import { cn } from '@/lib/utils';

interface Props {
  itemId: number;
  estado: Estado;
  /**
   * Es item hoja? Si no, marcar/desmarcar cascadea a todas las hojas
   * descendientes (con feedback en toast).
   */
  isLeaf: boolean;
  /** Puede editar? (agente del booking o admin) */
  canEdit: boolean;
  /** Solo lectura (viewing another agent's board) */
  readOnly?: boolean;
}

/**
 * Checkbox de "Completado / No completado".
 *
 * Internamente el schema sigue teniendo 4 estados (sin_iniciar, en_proceso,
 * completado, detenido) porque el pipeline de Cargowise los mapea desde el
 * status real. Pero la UI los colapsa a 2:
 *   - completado          → ✓ checked
 *   - cualquier otro      → ☐ unchecked
 *
 * Al desmarcar el item lo seteamos a `sin_iniciar` (estado "limpio").
 */
export function EstadoSelector({
  itemId,
  estado,
  isLeaf,
  canEdit,
  readOnly,
}: Props) {
  const qc = useQueryClient();
  const isCompleted = estado === 'completado';

  const updateEstado = useMutation({
    mutationFn: async (newEstado: Estado) => {
      const patch = (confirmado: boolean) =>
        api<{
          affectedLeaves?: number;
          bookingJustCompleted?: boolean;
          bookingIdExterno?: string;
        }>(`/items/${itemId}/estado`, {
          method: 'PATCH',
          body: JSON.stringify({ estado: newEstado, ...(confirmado ? { confirmado: true } : {}) }),
        });
      try {
        return await patch(false);
      } catch (err: any) {
        // Aviso previo: completar dejaría el embarque completado con
        // inconsistencia (CW temprano o pendientes ≠ 0). El usuario decide:
        // cancelar (no se marca, no hay correo) o confirmar (se marca y se
        // notifica a operativo + gerente + calidad@).
        if (err instanceof ApiError && err.status === 409 && err.body?.code === 'inconsistencia_completado') {
          const ok = window.confirm(
            `⚠️ ${err.body?.message ?? err.message}\n\n` +
              'Si confirmas, se marcará de todas formas y se notificará por correo ' +
              'al operativo, su gerente y calidad@.\n\n¿Marcar como completado?',
          );
          if (!ok) return { cancelado: true } as any;
          return await patch(true);
        }
        throw err;
      }
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['booking-items'] });
      qc.invalidateQueries({ queryKey: ['my-work'] });
      qc.invalidateQueries({ queryKey: ['my-work-count'] });
      qc.invalidateQueries({ queryKey: ['bookings'] });
      qc.invalidateQueries({ queryKey: ['item', itemId] });
      qc.invalidateQueries({ queryKey: ['item-history', itemId] });
      qc.invalidateQueries({ queryKey: ['team-agentes'] });

      if (res?.bookingJustCompleted) {
        toast.success(`🎉 Booking ${res.bookingIdExterno ?? ''} completado`, {
          description:
            'Todas sus tareas están terminadas. Lo encontrarás en el filtro "Completados".',
          duration: 6000,
        });
      } else if (!isLeaf && res?.affectedLeaves && res.affectedLeaves > 0) {
        toast.success(
          `${res.affectedLeaves} subtarea${res.affectedLeaves === 1 ? '' : 's'} actualizada${res.affectedLeaves === 1 ? '' : 's'}`,
        );
      }
    },
    onError: (err: any) => {
      toast.error(err instanceof ApiError ? err.message : 'Error al cambiar estado');
    },
  });

  const handleToggle = (e: React.MouseEvent | React.ChangeEvent) => {
    // Detener propagación para no abrir el modal del row.
    e.stopPropagation();
    if (!canEdit || readOnly || updateEstado.isPending) return;
    const target: Estado = isCompleted ? 'sin_iniciar' : 'completado';
    updateEstado.mutate(target);
  };

  // Solo-lectura (otro agente o sin permisos): badge estático
  const interactive = canEdit && !readOnly;

  const checkbox = (
    <span
      className={cn(
        'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors',
        isCompleted
          ? 'bg-emerald-500 border-emerald-500 text-white'
          : 'bg-surface-1 border-ink-300',
        interactive && 'hover:border-brand-500 cursor-pointer',
        !interactive && 'cursor-default',
        updateEstado.isPending && 'opacity-60',
      )}
      aria-checked={isCompleted}
      role="checkbox"
    >
      {isCompleted && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
    </span>
  );

  const label = (
    <span
      className={cn(
        'text-xs font-medium',
        isCompleted ? 'text-emerald-700' : 'text-ink-600',
      )}
    >
      {isCompleted ? 'Completado' : 'No completado'}
    </span>
  );

  if (!interactive) {
    return (
      <span className="inline-flex items-center gap-2" title={!isLeaf ? 'Se calcula desde sus subtareas' : undefined}>
        {checkbox}
        {label}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={handleToggle}
      disabled={updateEstado.isPending}
      className="inline-flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-brand-500 rounded px-1 -mx-1"
      aria-label={isCompleted ? 'Marcar como no completado' : 'Marcar como completado'}
      title={
        !isLeaf
          ? isCompleted
            ? 'Desmarcará todas las subtareas descendientes'
            : 'Marcará todas las subtareas como completadas'
          : undefined
      }
    >
      {checkbox}
      {label}
    </button>
  );
}
